import React, { useContext } from 'react';
import { Box, Text, ThemeContext } from 'grommet';
import { normalizeColor } from 'grommet/utils';
import useSWR from 'swr';
import { AGING_BALANCE_COLOR_MAP } from '../../constants/Colors';
import { followUpSummary } from '../../utils/chart-data';
import { nonRedirectFetcher } from '../../utils/errors';
import { formatCurrencySI } from '../../utils/numbers';
import ChartCard from './ChartCard';
import ChartLink from './ChartLink';
import ChartList from './ChartList';
import ChartRow from './ChartRow';
import { ChartRowsSkeleton } from './ChartSkeleton';
import SegmentBar from './SegmentBar';

const SEGMENTS = [
    ['not_yet_due', '<0'],
    ['days_1_30', '0+'],
    ['days_31_60', '30+'],
    ['days_61_90', '60+'],
    ['days_91_plus', '90+'],
];

const LATE_INVOICES = '/invoices/sent?invoice_state=between_61_and_90_days,over_91_days';

const VIEW_REPORT = <ChartLink href="/reports/aging-balance" label={<Text size="small" color="text-weak">View report</Text>} />;

// Who to chase first: the customers with the most money over 60 days past due.
const FollowUpFirst = ({ currency, rows = 5 }) => {
    const theme = useContext(ThemeContext);
    const { data, isLoading } = useSWR(
        '/api/reports/follow-up?limit=5',
        nonRedirectFetcher,
        { revalidateOnFocus: false, shouldRetryOnError: false },
    );

    if (isLoading) {
        return <ChartCard title="Follow up first" action={VIEW_REPORT}><ChartRowsSkeleton track rows={rows} /></ChartCard>;
    }
    if (!data) return null;

    const customers = (data.data || []).slice(0, rows);
    const largest = Math.max(...customers.map((customer) => customer.total));

    return (
        <ChartCard title="Follow up first" action={VIEW_REPORT}>
            {customers.length === 0 ? (
                <Text color="text-weak">
                    {data.total_receivables > 0 ? 'Nothing is more than 60 days past due.' : 'Customers with old balances appear here.'}
                </Text>
            ) : (
                <Box gap="small">
                    <Text weight={500}>{followUpSummary(customers, data.over_60_total)}</Text>
                    <ChartList>
                        {customers.map((customer) => (
                            <ChartRow
                                key={customer.uuid}
                                name={customer.name}
                                value={`${formatCurrencySI(customer.over_60, currency)} 61+ days`}
                                detail={`${Math.round(100 * customer.total / data.total_receivables)}% of AR`}
                                href={`${LATE_INVOICES}&customer_filter=customer:${customer.id}`}
                                event={{ section: 'follow_up_first', element: 'row' }}
                            >
                                <SegmentBar
                                    width={`${100 * customer.total / largest}%`}
                                    segments={SEGMENTS.map(([bucket, colorKey]) => ({
                                        key: bucket,
                                        value: customer.buckets?.[bucket],
                                        color: normalizeColor(AGING_BALANCE_COLOR_MAP[colorKey], theme),
                                    }))}
                                />
                            </ChartRow>
                        ))}
                    </ChartList>
                </Box>
            )}
        </ChartCard>
    );
};

export default FollowUpFirst;
